import {
  Button,
  ColumnLayout,
  Container,
  Flashbar,
  Header,
  Icon,
  Select,
  SpaceBetween,
  Spinner,
  Modal,
  Box,
} from "@amzn/awsui-components-react";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useChatOpen } from "../../contexes/ChatOpenContext";
import "./askTrackStars.scss";
import { SummaryItem } from "./SummaryItem";
import { useSummaryData } from "../../hooks/useSummaryData";
import ExternalLinks from "../ExternalLinks";
import { DigitalBusinessDocument } from "../../types/documents";
import { AccountingEventsGroup } from "../../types/events";
import HealthStatusIndicator from "../common/HealthStatusIndicator";
import { DlqInfo } from "../../hooks/useFetchDlqInfo";
import DlqStatusIndicator from "../dlq/DlqStatusIndicator";
import { screamingSnakeToTitleCase } from "../../helpers/dbdUtils";
import useFolders from "../../hooks/useFolders";
import { useFolderUI } from "../../hooks/useFolderUI";
import { FolderModal } from "../folders/FolderModal";
import { SavedSearchItem } from "../../types/common";
import { DevUserContext } from "../../contexes/OrderIdContext";
import { CanonicalBusinessEvent } from "../../types/cbes";
import {
  hasBundleOrder,
  hasMultipleProducts,
} from "../../helpers/bundleRevenueUtils";
import { BundleRevenueTimeline } from "../cbe/BundleRevenueTimeline";
import useShadowTest from "../../hooks/useShadowTest";
import useShadowTestResult from "../../hooks/useShadowTestResult";
import useClassifications from "../../hooks/useClassifications";
import usePermissions from "../../hooks/usePermissions";
import ShadowTestResultModal from "./ShadowTestResultModal";
import { MatchSetData } from "../../hooks/useFetchMatchSets";
import MatchSetStatusIndicator from "../common/MatchSetStatusIndicator";
import { isRevenueBookedByBothFlows } from "../../helpers/flowTypeUtils";
import {
  setModalParam,
  clearModalParam,
  readInitialModal,
} from "../../helpers/modalUrlUtils";
import { resolveShadowTestCountryCode } from "../../helpers/marketplaceUtils";
import useFetchSiblings from "../../hooks/useFetchSiblings";
import { getDigitalOrderId } from "../../helpers/linkUtils";
import {
  getShadowTestButtonIconName,
  getShadowTestResultStatus,
} from "../../helpers/shadowTestResultStatus";
import {
  SummaryEvent,
  SummaryOrderDetails,
} from "../../helpers/summaryDataUtils";
import {
  ComparisonEnvironment,
  ShadowTestResultResponse,
} from "../../lusca-search-api/generated-src";
import { onlyAvailableComparisonEnvironment } from "../../helpers/shadowTestComparisonEnvironment";

interface SummaryProps {
  eventData: SummaryEvent[];
  dlqInfo: DlqInfo;
  cbeData?: CanonicalBusinessEvent[];
  matchSetData?: MatchSetData;
  orderDetails?: SummaryOrderDetails;
}

export const getSiblingLookupId = (
  digitalOrderId: string,
  currentOrderId: string | undefined
) =>
  digitalOrderId && digitalOrderId !== currentOrderId
    ? digitalOrderId
    : undefined;

// Subtle inline trigger next to the transaction ID that opens the shared
// TrackStars chat panel. Renders nothing if no chat provider is present.
function AskTrackStars() {
  const chatOpen = useChatOpen();
  if (!chatOpen) {
    return null;
  }
  return (
    <button
      type="button"
      className="ask-trackstars"
      onClick={() => chatOpen.setIsOpen(true)}
      aria-label="Ask TrackStars about this transaction"
    >
      <span className="ask-trackstars__icon">
        <Icon name="gen-ai" size="small" />
      </span>
      <span className="ask-trackstars__label">Ask TrackStars</span>
    </button>
  );
}

export default function Summary({
  eventData,
  dlqInfo,
  cbeData = [],
  matchSetData,
  orderDetails,
}: SummaryProps) {
  const [, setSearchParams] = useSearchParams();
  const { folders, addTransactions, saveFolder } = useFolders();
  const {
    isModalVisible,
    isFolderModalVisible,
    selectedFolderId,
    folderName,
    error,
    setIsModalVisible,
    setSelectedFolderId,
    setFolderName,
    setNotification,
    handleModalDismiss,
    handleCreateFolder,
    handleFolderModalDismiss,
    handleSaveFolder,
    getFlashbarItems,
    flashbarStyles,
  } = useFolderUI(true);

  const [isBundleTimelineModalVisible, setIsBundleTimelineModalVisible] =
    React.useState(false);

  const {
    isLoading: isShadowTestLoading,
    error: shadowTestTriggerError,
    trigger: triggerShadowTest,
  } = useShadowTest();

  const [isShadowTestResultModalVisible, setIsShadowTestResultModalVisible] =
    React.useState(() => readInitialModal().modal === "shadow-test");
  const [selectedComparisonEnvironment, setSelectedComparisonEnvironment] =
    React.useState<ComparisonEnvironment>(() =>
      readInitialModal().comparisonEnvironment === ComparisonEnvironment.Beta
        ? ComparisonEnvironment.Beta
        : ComparisonEnvironment.Gamma
    );
  const [comparisonSelectionExplicit, setComparisonSelectionExplicit] =
    React.useState(
      () =>
        readInitialModal().comparisonEnvironment ===
          ComparisonEnvironment.Gamma ||
        readInitialModal().comparisonEnvironment === ComparisonEnvironment.Beta
    );
  const [initialShadowTab] = React.useState<string | undefined>(
    () => readInitialModal().stTab || undefined
  );
  const [initialShadowFilter] = React.useState<string>(
    () => readInitialModal().stFilter || "all"
  );
  const [initialShadowPropertyFilter] = React.useState(() => {
    const stQ = readInitialModal().stQ;
    if (!stQ) return undefined;
    try {
      return JSON.parse(
        stQ
      ) as import("@amzn/awsui-components-react").PropertyFilterProps.Query;
    } catch {
      return undefined;
    }
  });

  const {
    stage,
    orderId,
    countryCode,
    healthStatus,
    flowSteps,
    orderDate,
    glProductGroup,
    consumptionType,
    fulfillmentType,
    paymentFlow,
    paymentMethodClass,
    paymentMethod,
    isFreeTrial,
    isBundleImmediate,
    isSeasonPassBundle,
    isPreorderBundle,
    getGlMapping,
    lastItem,
    isHealthLoading,
    isMgm,
    declaredCountryCode,
  } = useSummaryData(eventData, dlqInfo, cbeData, orderDetails);

  const digitalOrderId = getDigitalOrderId(lastItem);
  const { siblings, isLoading: siblingsLoading } = useFetchSiblings(
    stage,
    getSiblingLookupId(digitalOrderId, orderId)
  );
  const siblingOptions = React.useMemo(
    () =>
      siblings.map((auid) => ({
        label: `Line Item: ${auid.slice(0, 8)}…`,
        description: auid,
        value: auid,
      })),
    [siblings]
  );

  const {
    gammaResult,
    betaResult,
    gammaIsLoading,
    betaIsLoading,
    gammaIsFetching,
    betaIsFetching,
    gammaError,
    betaError,
    refetch: refetchShadowTestResult,
    awaitNewResult: awaitNewShadowTestResult,
    awaitingEnvironment: awaitingShadowTestEnvironment,
  } = useShadowTestResult({
    stage,
    transactionId: orderId,
  });

  const previousShadowTestContext = React.useRef({ orderId, stage });
  React.useEffect(() => {
    const previous = previousShadowTestContext.current;
    if (previous.orderId === orderId && previous.stage === stage) return;
    previousShadowTestContext.current = { orderId, stage };
    setSelectedComparisonEnvironment(ComparisonEnvironment.Gamma);
    setComparisonSelectionExplicit(false);
  }, [orderId, stage]);

  React.useEffect(() => {
    if (comparisonSelectionExplicit || gammaIsLoading || betaIsLoading) {
      return;
    }
    const onlyEnvironment = onlyAvailableComparisonEnvironment(
      gammaResult,
      betaResult
    );
    if (onlyEnvironment) {
      setSelectedComparisonEnvironment(onlyEnvironment);
    }
  }, [
    betaIsLoading,
    betaResult,
    comparisonSelectionExplicit,
    gammaIsLoading,
    gammaResult,
  ]);

  const resultByComparisonEnvironment: Record<
    ComparisonEnvironment,
    ShadowTestResultResponse | null | undefined
  > = {
    [ComparisonEnvironment.Gamma]: gammaResult,
    [ComparisonEnvironment.Beta]: betaResult,
  };
  const shadowTestResult =
    resultByComparisonEnvironment[selectedComparisonEnvironment];
  const isShadowTestResultLoading =
    selectedComparisonEnvironment === ComparisonEnvironment.Beta
      ? betaIsLoading
      : gammaIsLoading;
  const isShadowTestResultFetching =
    selectedComparisonEnvironment === ComparisonEnvironment.Beta
      ? betaIsFetching
      : gammaIsFetching;
  const shadowTestResultError =
    selectedComparisonEnvironment === ComparisonEnvironment.Beta
      ? betaError
      : gammaError;
  const availableComparisonEnvironments = [
    ...(gammaResult ? [ComparisonEnvironment.Gamma] : []),
    ...(betaResult ? [ComparisonEnvironment.Beta] : []),
  ];

  const classifications = useClassifications(stage);
  const { canRedrive } = usePermissions();
  const devUser = React.useContext(DevUserContext);

  const isPaymentDocument = !!lastItem?.digitalPaymentDocument;

  const formattedConsumptionType = consumptionType
    ? screamingSnakeToTitleCase(consumptionType.toUpperCase())
    : null;

  const handleFolderSelect = () => {
    if (selectedFolderId && orderId) {
      const newTransaction: SavedSearchItem = {
        date: new Date().toISOString(),
        id: orderId,
        stage,
        user: devUser,
        status: healthStatus,
        country: countryCode || "",
        gl: String(glProductGroup || ""),
        usecase: "",
        paymentMethod: paymentMethod || "",
        labels: [],
        consumptionTypes: [],
      };

      const result = addTransactions(selectedFolderId, [newTransaction]);

      setNotification({
        type: result.success ? "success" : "error",
        message: result.message,
      });
      handleModalDismiss();
    }
  };

  // Check if bundle revenue timeline should be shown
  // Support both immediate bundles and season pass bundles
  const shouldShowBundleTimeline =
    cbeData &&
    cbeData.length > 0 &&
    cbeData.some(
      (cbe) =>
        cbe.internalAttributes?.IS_BUNDLE_IMMEDIATE === "true" ||
        cbe.internalAttributes?.IS_SEASON_PASS_BUNDLE === "true"
    ) &&
    hasBundleOrder(cbeData) &&
    hasMultipleProducts(cbeData);

  const dbdData = React.useMemo(() => {
    const dbd = eventData.find(
      (item): item is DigitalBusinessDocument =>
        "body" in item && item.documentType === "DigitalBusinessDocument"
    );

    if (!dbd)
      return {
        products: undefined,
        totalOrderValue: undefined,
        productCount: undefined,
      };

    const products = dbd.body?.body?.products;
    const payments = dbd.body?.body?.payments;

    let totalOrderValue: number | undefined;
    if (payments) {
      const firstPayment = Object.values(payments)[0];
      const amountStr = firstPayment?.paymentMethods
        ? Object.values(firstPayment.paymentMethods)[0]?.amount
            ?.customerTotalAmountWithoutTax?.value
        : undefined;
      const parsed = amountStr ? parseFloat(amountStr) : undefined;
      totalOrderValue = parsed && !Number.isNaN(parsed) ? parsed : undefined;
    }

    const productCount = products ? Object.keys(products).length : undefined;

    return { products, totalOrderValue, productCount };
  }, [eventData]);

  const getShadowTestEventData = () => {
    const accountingEventsGroup = eventData.find(
      (item): item is AccountingEventsGroup => "accountingEvents" in item
    );
    const firstEvent = accountingEventsGroup
      ? Object.values(accountingEventsGroup.accountingEvents)[0]
      : null;
    return {
      auid: firstEvent?.accountingUniqueId || "",
      subscriptionId: firstEvent?.subscriptionId || "",
      subscriptionPeriod: firstEvent?.subscriptionPeriod || "",
      digitalOrderID: firstEvent?.orderId || "",
      deliveryPacketItemId: firstEvent?.deliveryPacketItemId || "",
      financeTagAuthReferenceId: firstEvent?.financeTagAuthReferenceId || "",
      subCountryCode: firstEvent?.declaredCountryCode || "",
    };
  };

  const generateShadowTestCommand = () => {
    try {
      const {
        auid,
        subscriptionId,
        subscriptionPeriod,
        digitalOrderID,
        deliveryPacketItemId,
        financeTagAuthReferenceId,
        subCountryCode,
      } = getShadowTestEventData();
      const marketplace = countryCode.replace("_PROD", "");
      // Keep the -cc flag consistent with what the UI trigger button sends via the API, so the
      // copy-paste CLI command selects the same JEM file (see onTriggerTest below).
      const resolvedCountryCode = resolveShadowTestCountryCode(
        marketplace,
        declaredCountryCode,
        subCountryCode
      );
      const comparisonArgument =
        selectedComparisonEnvironment === ComparisonEnvironment.Beta
          ? " -comparisonEnvironment BETA"
          : "";

      return `brazil-build shadow-test --args="-m ${marketplace} -cc ${resolvedCountryCode} -gl ${
        glProductGroup ?? ""
      } -o ${orderId}|${auid}|${subscriptionId}|${subscriptionPeriod}|${deliveryPacketItemId}|${digitalOrderID}|${financeTagAuthReferenceId}|${comparisonArgument}"`;
    } catch (cmdError) {
      const comparisonArgument =
        selectedComparisonEnvironment === ComparisonEnvironment.Beta
          ? " -comparisonEnvironment BETA"
          : "";
      return `brazil-build shadow-test --args="-m ${countryCode} -gl ${
        glProductGroup ?? ""
      } -o ${orderId}${comparisonArgument}"`;
    }
  };

  return (
    <>
      <div style={flashbarStyles}>
        <Flashbar items={getFlashbarItems()} />
      </div>
      <Container
        header={
          <Header
            variant="h1"
            actions={
              <SpaceBetween direction="horizontal" size="xxs">
                {siblingsLoading && <Spinner size="normal" />}
                {siblingOptions.length > 1 && (
                  <Select
                    selectedOption={
                      siblingOptions.find((opt) => opt.value === orderId) ||
                      null
                    }
                    options={siblingOptions}
                    onChange={({ detail }) => {
                      if (
                        detail.selectedOption.value &&
                        detail.selectedOption.value !== orderId
                      ) {
                        setSearchParams({
                          stage,
                          transactionId: detail.selectedOption.value,
                        });
                      }
                    }}
                  />
                )}
                <Button
                  disabled={!orderId}
                  onClick={() => {
                    setIsShadowTestResultModalVisible(true);
                    setModalParam("shadow-test", {
                      comparisonEnvironment: selectedComparisonEnvironment,
                    });
                  }}
                  iconName={getShadowTestButtonIconName(shadowTestResult)}
                >
                  {shadowTestResult
                    ? `Shadow Test: ${getShadowTestResultStatus(
                        shadowTestResult
                      )}`
                    : "Shadow Test Results"}
                </Button>
                {shouldShowBundleTimeline && (
                  <Button
                    onClick={() => setIsBundleTimelineModalVisible(true)}
                    iconName="status-in-progress"
                  >
                    Bundle Revenue Timeline
                  </Button>
                )}
                <Button
                  onClick={() => setIsModalVisible(true)}
                  disabled={isPaymentDocument}
                >
                  Add to Folder
                </Button>
              </SpaceBetween>
            }
          >
            <span className="summary-title">
              {orderId}
              <AskTrackStars />
            </span>
          </Header>
        }
      >
        <ColumnLayout columns={5} borders="vertical">
          <SummaryItem label="Stage" value={stage} />
          {devUser && <SummaryItem label="User" value={devUser} />}
          <SummaryItem
            label="GL"
            value={glProductGroup ? getGlMapping(glProductGroup) : "-"}
          />
          <SummaryItem label="Country" value={countryCode} />
          {paymentMethod && (
            <SummaryItem
              label="Payment"
              value={
                isFreeTrial ? `Free Trial (${paymentMethod})` : paymentMethod
              }
            />
          )}
          <SummaryItem
            label="Order Date (UTC)"
            value={
              orderDate
                ? new Date(orderDate).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    timeZone: "UTC",
                  })
                : "-"
            }
          />
          {formattedConsumptionType && (
            <SummaryItem label="Consumption" value={formattedConsumptionType} />
          )}
          {fulfillmentType && (
            <SummaryItem
              label="Fulfillment"
              value={screamingSnakeToTitleCase(fulfillmentType)}
            />
          )}
          {paymentFlow && (
            <SummaryItem label="Payment Flow" value={paymentFlow} />
          )}
          {paymentMethodClass && (
            <SummaryItem
              label="Payment Method Class"
              value={paymentMethodClass}
            />
          )}
          {/* TEMP FIX: Hide bundle flags when all are "No" to reduce clutter.
             Only show the ones that are "Yes". Remove this when bundle display is redesigned. */}
          {isBundleImmediate && (
            <SummaryItem label="Immediate Bundle" value="Yes" />
          )}
          {isSeasonPassBundle && (
            <SummaryItem label="Season Pass Bundle" value="Yes" />
          )}
          {isPreorderBundle && (
            <SummaryItem label="Preorder Bundle" value="Yes" />
          )}
          {isMgm && <SummaryItem label="MGM" value="Yes" />}
          <SummaryItem
            label="Status"
            value={
              <HealthStatusIndicator
                healthStatus={healthStatus}
                flowSteps={flowSteps}
                isLoading={isHealthLoading}
                eventData={eventData}
                cbeData={cbeData}
              />
            }
          />
          {matchSetData && (
            <SummaryItem
              label="Match Sets"
              value={
                <MatchSetStatusIndicator
                  matchSetData={matchSetData}
                  isDualFlow={isRevenueBookedByBothFlows(eventData, cbeData)}
                />
              }
            />
          )}
          <SummaryItem
            label="DLQ Status"
            value={
              <DlqStatusIndicator
                status={dlqInfo.status}
                dlqItems={dlqInfo.dlqItems}
                stage={stage}
                transactionId={orderId}
              />
            }
          />
          <SummaryItem
            label="External Links"
            value={
              <ExternalLinks
                {...lastItem}
                cbeIdempotenceId={
                  isPaymentDocument ? cbeData[0]?.idempotenceId : undefined
                }
              />
            }
          />
        </ColumnLayout>
      </Container>

      <FolderModal
        isModalVisible={isModalVisible}
        isFolderModalVisible={isFolderModalVisible}
        isShareModalVisible={false}
        selectedFolderId={selectedFolderId}
        folderName={folderName}
        error={error}
        folders={folders}
        sharingFolder={null}
        onModalDismiss={handleModalDismiss}
        onCreateFolder={handleCreateFolder}
        onFolderSelect={handleFolderSelect}
        onFolderModalDismiss={handleFolderModalDismiss}
        onShareModalDismiss={() => {}}
        onSaveFolder={() => handleSaveFolder(saveFolder)}
        setSelectedFolderId={setSelectedFolderId}
        setFolderName={setFolderName}
      />

      {shouldShowBundleTimeline && (
        <Modal
          onDismiss={() => setIsBundleTimelineModalVisible(false)}
          visible={isBundleTimelineModalVisible}
          size="max"
          footer={
            <Box float="right">
              <Button
                variant="primary"
                onClick={() => setIsBundleTimelineModalVisible(false)}
              >
                Close
              </Button>
            </Box>
          }
          header="Bundle Revenue Timeline"
        >
          <BundleRevenueTimeline
            cbes={cbeData}
            totalOrderValue={dbdData.totalOrderValue}
            productCount={dbdData.productCount}
            dbdProducts={dbdData.products}
          />
        </Modal>
      )}

      <ShadowTestResultModal
        visible={isShadowTestResultModalVisible}
        onDismiss={() => {
          setIsShadowTestResultModalVisible(false);
          clearModalParam();
        }}
        result={shadowTestResult}
        isLoading={isShadowTestResultLoading}
        isRefreshing={isShadowTestResultFetching}
        isAwaitingResult={
          awaitingShadowTestEnvironment === selectedComparisonEnvironment
        }
        error={shadowTestResultError}
        onRefresh={() => {
          refetchShadowTestResult().catch((refreshError) => {
            console.error(
              "Failed to refresh shadow test result:",
              refreshError
            );
          });
        }}
        cliCommand={canRedrive ? generateShadowTestCommand() : undefined}
        classifications={classifications}
        comparisonEnvironment={selectedComparisonEnvironment}
        availableComparisonEnvironments={availableComparisonEnvironments}
        onComparisonEnvironmentChange={(environment) => {
          setSelectedComparisonEnvironment(environment);
          setComparisonSelectionExplicit(true);
        }}
        onTriggerTest={(comparisonEnvironment) => {
          const targetEnvironment =
            comparisonEnvironment ?? ComparisonEnvironment.Gamma;
          setSelectedComparisonEnvironment(targetEnvironment);
          setComparisonSelectionExplicit(true);
          const {
            auid,
            subscriptionId,
            subscriptionPeriod,
            digitalOrderID,
            deliveryPacketItemId,
            financeTagAuthReferenceId,
            subCountryCode,
          } = getShadowTestEventData();
          const marketplace = countryCode?.replace("_PROD", "") || "";
          triggerShadowTest(
            {
              stage,
              marketplace,
              glProductGroupId: String(glProductGroup || ""),
              transactionIds: [orderId],
              countryCode: resolveShadowTestCountryCode(
                marketplace,
                declaredCountryCode,
                subCountryCode
              ),
              auid: auid || undefined,
              subscriptionId: subscriptionId || undefined,
              periodNumber: subscriptionPeriod
                ? String(subscriptionPeriod)
                : undefined,
              deliveryPacketItemId: deliveryPacketItemId
                ? String(deliveryPacketItemId)
                : undefined,
              digitalOrderID: digitalOrderID || undefined,
              financeTagAuthReferenceId: financeTagAuthReferenceId || undefined,
              comparisonEnvironment,
            },
            () => awaitNewShadowTestResult(targetEnvironment)
          );
        }}
        isTriggerLoading={isShadowTestLoading}
        triggerError={shadowTestTriggerError}
        canTrigger={!isPaymentDocument && !!orderId}
        initialTab={initialShadowTab}
        initialFilter={initialShadowFilter}
        initialPropertyFilter={initialShadowPropertyFilter}
      />
    </>
  );
}
